import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, AlertTriangle, Heart, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'suggestion', icon: Lightbulb, color: '#2563eb' },
  { id: 'problem', icon: AlertTriangle, color: '#dc2626' },
  { id: 'feature', icon: Sparkles, color: '#7c3aed' },
  { id: 'praise', icon: Heart, color: '#16a34a' },
];

export default function FeedbackForm({ getHeaders, onSubmitted, compact = false }) {
  const { t } = useTranslation('dashboard');
  const [category, setCategory] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/client/feedback/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({
          category,
          message: message.trim(),
          page: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        setMessage('');
        if (onSubmitted) onSubmitted(data);
      } else {
        setError(data.detail || t('feedback.error', 'Could not submit. Please try again.'));
      }
    } catch (err) {
      setError(t('feedback.error', 'Could not submit. Please try again.'));
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="feedback-success">
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-7 h-7 text-green-600" />
        </div>
        <h3 className="font-semibold text-gray-900">{t('feedback.thanksTitle', 'Thank you!')}</h3>
        <p className="text-sm text-gray-500 mt-1">{t('feedback.thanksSub', 'We read every message and use it to improve.')}</p>
        <Button variant="outline" className="mt-4" onClick={() => setDone(false)} data-testid="feedback-send-another">
          {t('feedback.sendAnother', 'Send another')}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">{t('feedback.typeLabel', 'What kind of feedback?')}</p>
        <div className="grid grid-cols-4 gap-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs transition-all ${active ? 'border-transparent text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                style={active ? { backgroundColor: c.color } : {}}
                data-testid={`feedback-category-${c.id}`}
              >
                <Icon className="h-4 w-4" />
                {t(`feedback.categories.${c.id}`, c.id)}
              </button>
            );
          })}
        </div>
      </div>
      <Textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t('feedback.placeholder', 'Tell us what is working, what is not, or what you wish the platform could do...')}
        rows={compact ? 4 : 5}
        required
        data-testid="feedback-message"
      />
      {error && <p className="text-sm text-red-600" data-testid="feedback-error">{error}</p>}
      <Button type="submit" disabled={submitting || !message.trim()} className="w-full" data-testid="feedback-submit">
        {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('feedback.sending', 'Sending...')}</> : t('feedback.submit', 'Send Feedback')}
      </Button>
    </form>
  );
}
